import { ReactNode, useState } from 'react';

import { Button, Group, Pagination, Select, Table, Text } from '@mantine/core';

import { SCUD_EMPTY, SCUD_PAGE_SIZES } from '@/shared/constant/scudMapping';
import { noop } from '@/shared/helpers/noop';
import IconDelete from '@/shared/uikit/assets/icons/IconDelete.svg?react';
import IconEdit from '@/shared/uikit/assets/icons/IconEdit.svg?react';
import { PopConfirm } from '@/shared/uikit/components/popovers';

/** Общий вид таблиц экрана: «Пропуска», «Исключения», «Причины» */
export const ScudTable = ({ children, miw }: { children: ReactNode; miw?: number }) => (
	<Table verticalSpacing="xs" horizontalSpacing={6} stickyHeader highlightOnHover miw={miw}>
		{children}
	</Table>
);

export const ScudTh = ({ children, width }: { children?: ReactNode; width?: number | string }) => (
	<Table.Th w={width}>
		<Text size="sm" c="dimmed" fw={600}>
			{children}
		</Text>
	</Table.Th>
);

/** Основное значение и мелкая вторая строка */
export const CellLines = ({
	main,
	sub,
}: {
	main: string | null | undefined;
	sub?: string | null;
}) => (
	<>
		<Text size="sm" fw={600}>
			{main || SCUD_EMPTY}
		</Text>
		{sub && (
			<Text size="xs" c="dimmed">
				{sub}
			</Text>
		)}
	</>
);

export const pageCount = (total: number, pageSize: number) =>
	Math.max(1, Math.ceil(total / pageSize));

type ScudTablePagerProps = {
	page: number;
	pageSize: number;
	total: number;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
};

export const ScudTablePager = ({
	page,
	pageSize,
	total,
	onPageChange,
	onPageSizeChange,
}: ScudTablePagerProps) => {
	const pages = pageCount(total, pageSize);
	const from = total ? (page - 1) * pageSize + 1 : 0;
	const to = Math.min(page * pageSize, total);

	return (
		<Group justify="space-between">
			<Group gap="xs">
				<Text size="sm" c="dimmed">
					Показано {from}–{to} из {total} · строк на странице:
				</Text>
				<Select
					size="xs"
					w={80}
					data={SCUD_PAGE_SIZES}
					value={`${pageSize}`}
					onChange={(value) => onPageSizeChange(Number(value))}
					allowDeselect={false}
				/>
			</Group>
			{pages > 1 && <Pagination total={pages} value={page} onChange={onPageChange} size="sm" />}
		</Group>
	);
};

/** По возрастанию → по убыванию → снова сортировка по умолчанию */
export const useTableSort = <T extends string>(defaultField: T, onChange?: () => void) => {
	const [sort, setSort] = useState<T>(defaultField);
	const [desc, setDesc] = useState(false);

	const onSort = (field: T) => {
		if (field !== sort) {
			setSort(field);
			setDesc(false);
		} else if (!desc) {
			setDesc(true);
		} else {
			setSort(defaultField);
			setDesc(false);
		}

		onChange?.();
	};

	return { sort, desc, onSort };
};

const iconButton = {
	variant: 'transparent',
	size: 'compact-sm',
	radius: 'sm',
	color: 'black',
} as const;

export const EditIconButton = ({ onClick }: { onClick: () => void }) => (
	<Button {...iconButton} onClick={onClick} aria-label="Изменить" title="Изменить">
		<IconEdit fontSize={20} color="#667085" />
	</Button>
);

export const DeleteIconButton = ({
	confirmText,
	onOk,
}: {
	confirmText: string;
	onOk: () => void;
}) => (
	<PopConfirm
		confirmText={confirmText}
		position="top"
		withArrow
		onOk={onOk}
		onCancel={noop}
		button={{
			...iconButton,
			children: <IconDelete fontSize={20} color="#667085" role="img" aria-label="Удалить" />,
		}}
	/>
);
